import { LuShrink } from 'react-icons/lu';
import { ChevronDownIcon } from 'lucide-react';
import React from 'react';
import { useOrganization } from '../../../hooks/useOrganization';
import { useUsers } from '../../../hooks/useUsers';
import home from '../../../components/sidebar/assets/home.svg';
import leads from '../../../components/sidebar/assets/leads.svg';
import intent from '../../../components/sidebar/assets/intent.svg';
import menu from '../../../components/navbar/assets/menu.svg';
import sort from '../../../assets/sort.svg';
import filter from '../../../assets/filter.svg';
import question from '../../../assets/question.svg';

interface WebsitePrevProps {
  showRole?: boolean;
  role?: string;
  companyName?: string;
}

const getInitialColor = (initial: string) => {
  const colors = [
    { bg: 'bg-[#F57F17]', text: 'text-white' },
    { bg: 'bg-[#1976D2]', text: 'text-white' },
    { bg: 'bg-[#388E3C]', text: 'text-white' },
    { bg: 'bg-[#8E24AA]', text: 'text-white' },
  ];
  
  const length = initial.length;
  const index = Math.min(length - 1, 3);
  return colors[index];
};

const WebsitePrev: React.FC<WebsitePrevProps> = ({ 
  showRole = false, 
  role,
  companyName
}) => {
  const { organization } = useOrganization();
  const { user } = useUsers();
  
  // Get company name with fallback chain
  const displayCompanyName = companyName || 
                           organization?.linkedin_company_profile?.name || 
                           organization?.name ||
                           'Workspace title';
  
  // Get role with fallback chain
  const displayRole = role || 
                     user?.role ||
                     'Role';

  const initial = displayRole?.[0]?.toUpperCase() || 'R';
  const { bg, text } = getInitialColor(initial);
  
  return (
    <div className="flex justify-center">
      <div className="w-[663px] h-[462px] bg-[#FFFFFF] rounded-[16px] shadow-[0px_8px_28px_-6px_#1C28401F] ml-[132px]">
        <div className="flex h-full">
          {/* Sidebar */}
          <div className="w-[143px] border-r border-[#EEEFF1] h-full relative">
            {/* Workspace Header */}
            <div className="h-[28px] flex items-center justify-between px-2 border-b border-[#EEEFF1]">
              <div className="flex items-center gap-[6px]">
                <div className="w-[12px] h-[12px] bg-[#29DC5B] rounded-[4px]"></div>
                <div className="w-fit gap-[4px] flex items-center">
                  <span className="text-[10px] text-[#222222] font-bold  truncate max-w-[100px]">
                    {displayCompanyName}
                  </span>
                  <ChevronDownIcon className="w-[8px] h-[8px] text-[#75777C]" />
                </div>
              </div>
              <div className="flex items-center gap-[6px]">
                <LuShrink className="w-[14px] h-[14px] text-[#75777C]" />
              </div>
            </div>

            {/* Role Card - Show as floating card if showRole is true */}
            {showRole && (
              <div className="absolute top-[28px] left-0 right-0 z-10">
                <div className="mx-2 mt-1 bg-white rounded-[8px] shadow-[0px_2px_8px_rgba(0,0,0,0.1)] border border-[#EEEEEE] p-3 w-[200px]">
                  <div className="flex items-center gap-3">
                    <div className={`min-w-[24px] w-[24px] h-[24px] rounded-[2px] ${bg} flex items-center justify-center text-[12px] ${text}`}>
                      {initial}
                    </div>
                    <span className="text-[14px] text-[#222222] font-normal  truncate rounded-[4px]">{displayRole}</span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Sidebar Menu Items */}
            <div className="flex flex-col bg-[#FAFAFA] items-start justify-between h-[434px] rounded-bl-[16px]">
              <div className="flex flex-col items-start justify-center py-2">
                <div className="px-2 py-1.5 flex items-center gap-2">
                  <img src={home} alt="Home" className="w-[10px] h-[10px]" />
                  <div className="w-[30px] h-[5px] bg-[#EEEEEE] rounded-[4px]"></div>
                </div>
                <div className="px-2 py-1.5 flex items-center gap-2">
                  <img src={leads} alt="Leads" className="w-[10px] h-[10px]" />
                  <div className="w-[30px] h-[5px] bg-[#EEEEEE] rounded-[4px]"></div>
                </div>
                <div className="px-2 py-1.5 flex items-center gap-2">
                  <img src={intent} alt="Intent Signals" className="w-[10px] h-[10px]" />
                  <div className="w-[30px] h-[5px] bg-[#EEEEEE] rounded-[4px]"></div>
                </div>
              </div>
              
              <div className="flex flex-col items-end justify-center px-[8px] gap-[8px] border-t border-[#EEEFF1]">
                <div className="flex flex-col items-center justify-between my-[8px]">
                  <div className='flex items-center my-[8px] gap-[10px]'>
                    <img src={question} alt="help" className="w-[14px] h-[14px]" />
                    <div className="w-[98px] h-[5px] bg-[#EEEEEE] rounded-[4px]"></div>
                  </div>
                  <div className="flex items-center gap-[8px] w-full bg-[#FFFFFF] rounded-[6px] px-[4px] py-[4px]">
                    <div className="text-[7px] font-normal text-[#12692B] bg-[#81F4A1] rounded-[4px] px-[4px]">7</div>
                    <div className="w-[98px] h-[5px] bg-[#EEEEEE] rounded-[4px]"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Top Navigation */}
            <div className="h-[28px] border-b border-[#EEEFF1] flex items-center px-2">
              <div className="flex items-center gap-2">
                <img src={menu} alt="Menu" className="w-[8px] h-[8px]" />
                <div className="w-[44px] h-[5px] bg-[#EEEEEE] rounded-[4px]"></div>
              </div>
            </div>

            {/* Filter and Sort */}
            <div className='flex items-center bg-[#FFFFFF] py-[6px] px-[8px] border-b border-[#EEEFF1]'>
              <div className='flex items-center gap-1'>
                <div className="flex items-center gap-1 px-1.5 py-0.5 text-[#7A7A7A] bg-[#FFFFFF] border border-[#E6E7EA] rounded">
                  <img src={menu} alt="menu" className="w-[6px] h-[6px]" />
                  <span className="text-[8px] font-normal ">All Companies</span>
                </div>
              </div>
            </div>
            <div className='flex items-center bg-white py-[6px] px-[8px] border-b border-[#EEEFF1]'>
              <div className='flex items-center gap-1'>
                <div className="flex items-center gap-1 px-1.5 py-0.5 text-[#7A7A7A] bg-[#FAFAFA] border border-[#E6E7EA] rounded">
                  <img src={sort} alt="sort" className="w-[6px] h-[6px]" />
                  <span className="text-[8px] font-normal ">Sort by</span>
                </div>
                <div className="text-[#DDDDDD] text-[8px] text-center pb-[2px] font-thin">|</div>
                <div className="flex items-center gap-1 px-1.5 py-0.5 text-[#7A7A7A] bg-[#FAFAFA] border border-[#E6E7EA] rounded">
                  <img src={filter} alt="filter" className="w-[6px] h-[6px]" />
                  <span className="text-[8px] font-normal ">Filter</span>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="w-full">
              <table className="w-full table-fixed">
                <thead>
                  <tr className="border-b bg-[#FFFFFF]">
                    <th className="pl-4 w-[24px] h-[22px]">
                      <div className="h-[8px] w-[8px] border border-[#EEEEEE] rounded-[2px]"></div>
                    </th>
                    {[...Array(4)].map((_, idx) => (
                      <th 
                        key={idx}
                        className="text-left p-2 border-r border-[#EEEEEE] h-[22px]"
                      >
                        <div className="flex items-center gap-1">
                          <img src={intent} alt="intent" className="w-[6px] h-[6px]" />
                          <div className="h-[4px] bg-[#EEEEEE] rounded w-[70px]"></div>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...Array(9)].map((_, index) => (
                    <tr 
                      key={index} 
                      className="animate-pulse border-b border-[#EEEEEE]"
                    >
                      <td className="pl-4 w-[24px] h-[22px]">
                        <div className="h-[8px] w-[8px] border border-[#EEEEEE] rounded-[2px]"></div>
                      </td>
                      {[...Array(4)].map((_, idx) => (
                        <td 
                          key={idx} 
                          className={`px-3 border-r border-[#EEEEEE] ${
                            idx % 2 === 0 ? 'bg-[#FFFFFF]' : 'bg-[#F9FBFE]'
                          }`}
                        >
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between py-2 px-3">
              <div className="flex items-center gap-2 text-[8px] text-[#75777C]"> 1-5 of 5</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebsitePrev;
